import { io, type Socket } from 'socket.io-client';
import {
  SOCKET_PATH,
  type BroadcastEvent,
  type BroadcastState,
  type BroadcastTick,
  type ClientToServerEvents,
  type ServerToClientEvents,
} from '@fbwl/shared';

export type ConnectionStatus = 'connecting' | 'live' | 'reconnecting' | 'offline';

type EventHandler = (event: BroadcastEvent) => void;
type StateHandler = (state: BroadcastState) => void;
type TickHandler = (tick: BroadcastTick) => void;
type StatusHandler = (status: ConnectionStatus) => void;

/**
 * The overlay's link to the arena.
 *
 * Holds the last snapshot and the last tick so any part of the renderer can ask
 * "what is true right now" without threading state through constructors, and so
 * a scene created mid-phase starts correct rather than empty.
 *
 * Reconnection is Socket.IO's, with unlimited attempts: an overlay that gives up
 * is a black screen on a live channel.
 */
export class BroadcastConnection {
  private readonly socket: Socket<ServerToClientEvents, ClientToServerEvents>;
  private readonly eventHandlers = new Set<EventHandler>();
  private readonly stateHandlers = new Set<StateHandler>();
  private readonly tickHandlers = new Set<TickHandler>();
  private readonly statusHandlers = new Set<StatusHandler>();

  private latestState: BroadcastState | null = null;
  private latestTick: BroadcastTick | null = null;
  private status: ConnectionStatus = 'connecting';

  /** serverTime - clientTime, so local countdowns match the arena clock. */
  private clockOffset = 0;

  constructor(url: string) {
    this.socket = io(url, {
      path: SOCKET_PATH,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 700,
      reconnectionDelayMax: 6000,
      timeout: 12_000,
    });

    this.socket.on('connect', () => {
      this.setStatus('live');
      this.syncClock();
    });
    this.socket.on('disconnect', () => this.setStatus('reconnecting'));
    this.socket.io.on('reconnect_attempt', () => this.setStatus('reconnecting'));
    this.socket.io.on('error', () => this.setStatus('offline'));

    this.socket.on('state:snapshot', (state) => {
      this.latestState = state;
      for (const handler of this.stateHandlers) handler(state);
    });

    this.socket.on('state:tick', (tick) => {
      this.latestTick = tick;
      for (const handler of this.tickHandlers) handler(tick);
    });

    this.socket.on('broadcast:event', (event) => {
      for (const handler of this.eventHandlers) handler(event);
    });

    this.socket.on('server:shutdown', () => this.setStatus('reconnecting'));
  }

  get state(): BroadcastState | null {
    return this.latestState;
  }

  get tick(): BroadcastTick | null {
    return this.latestTick;
  }

  get connectionStatus(): ConnectionStatus {
    return this.status;
  }

  /** Arena time, corrected for the round trip measured at connect. */
  now(): number {
    return Date.now() + this.clockOffset;
  }

  onState(handler: StateHandler): () => void {
    this.stateHandlers.add(handler);
    if (this.latestState) handler(this.latestState);
    return () => this.stateHandlers.delete(handler);
  }

  onTick(handler: TickHandler): () => void {
    this.tickHandlers.add(handler);
    return () => this.tickHandlers.delete(handler);
  }

  onEvent(handler: EventHandler): () => void {
    this.eventHandlers.add(handler);
    return () => this.eventHandlers.delete(handler);
  }

  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    handler(this.status);
    return () => this.statusHandlers.delete(handler);
  }

  authenticate(token: string): Promise<{ ok: boolean; message: string }> {
    return new Promise((resolve) => {
      this.socket.emit('admin:auth', token, resolve);
    });
  }

  command(command: Parameters<ClientToServerEvents['admin:command']>[0]) {
    return new Promise<{ ok: boolean; message: string }>((resolve) => {
      this.socket.emit('admin:command', command, resolve);
    });
  }

  dispose(): void {
    this.socket.close();
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    for (const handler of this.statusHandlers) handler(status);
  }

  /** Three probes, keep the one with the lowest round trip. */
  private syncClock(): void {
    let bestRtt = Number.POSITIVE_INFINITY;

    const probe = (remaining: number): void => {
      const sent = Date.now();
      this.socket.emit('time:sync', sent, (serverTime: number) => {
        const rtt = Date.now() - sent;
        if (rtt < bestRtt) {
          bestRtt = rtt;
          this.clockOffset = serverTime + rtt / 2 - Date.now();
        }
        if (remaining > 0) setTimeout(() => probe(remaining - 1), 250);
      });
    };

    probe(2);
  }
}
